import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type {
  CalculatorScenario,
  CalculatorScenarioSlot,
  CalculatorScenarioHardware,
} from '../lib/database.types'

export const CALC_SCENARIOS_KEY = ['calculator_scenarios']

/** Full scenario assembled from its 3 tables — what the calculator round-trips. */
export interface CalculatorScenarioFull {
  scenario: CalculatorScenario
  slots: CalculatorScenarioSlot[]
  hardware: CalculatorScenarioHardware[]
}

export interface SaveScenarioInput {
  /** When set, update; when null/undefined, create new. */
  id?: string | null
  product_id: string | null
  printer_id: string | null
  name: string
  model_link: string | null
  print_time_hrs: number
  print_time_mins: number
  units_per_plate: number
  handling_minutes_per_unit: number
  sale_price_per_unit: number
  moms_cut_pct_override: number | null
  slots: Array<{
    slot_index: number
    spool_id: string | null
    model_grams: number
    support_grams: number
    purge_grams: number
  }>
  hardware: Array<{
    name: string
    quantity: number
    unit_cost: number
  }>
}

/** List the user's saved scenarios (header rows only, newest first). */
export function useCalculatorScenarios() {
  return useQuery({
    queryKey: CALC_SCENARIOS_KEY,
    queryFn: async (): Promise<CalculatorScenario[]> => {
      const { data, error } = await supabase
        .from('calculator_scenarios')
        .select('*')
        .order('updated_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

/** Load one scenario in full (header + slots + hardware). */
export async function fetchScenarioFull(id: string): Promise<CalculatorScenarioFull> {
  const [headerRes, slotsRes, hwRes] = await Promise.all([
    supabase.from('calculator_scenarios').select('*').eq('id', id).single(),
    supabase.from('calculator_scenario_slots').select('*').eq('scenario_id', id).order('slot_index'),
    supabase.from('calculator_scenario_hardware').select('*').eq('scenario_id', id).order('sort_order'),
  ])
  if (headerRes.error) throw headerRes.error
  if (slotsRes.error) throw slotsRes.error
  if (hwRes.error) throw hwRes.error
  return {
    scenario: headerRes.data,
    slots: slotsRes.data ?? [],
    hardware: hwRes.data ?? [],
  }
}

/**
 * Save or update a full scenario in one round-trip-ish.
 * Strategy: upsert the header, then delete-and-reinsert slots + hardware.
 * Simpler than diffing, and the children are short.
 */
export function useSaveCalculatorScenario() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SaveScenarioInput): Promise<string> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')

      let scenarioId: string
      if (input.id) {
        const { error } = await supabase
          .from('calculator_scenarios')
          .update({
            product_id: input.product_id,
            printer_id: input.printer_id,
            name: input.name,
            model_link: input.model_link,
            print_time_hrs: input.print_time_hrs,
            print_time_mins: input.print_time_mins,
            units_per_plate: input.units_per_plate,
            handling_minutes_per_unit: input.handling_minutes_per_unit,
            sale_price_per_unit: input.sale_price_per_unit,
            moms_cut_pct_override: input.moms_cut_pct_override,
            updated_at: new Date().toISOString(),
          })
          .eq('id', input.id)
        if (error) throw error
        scenarioId = input.id
      } else {
        const { data, error } = await supabase
          .from('calculator_scenarios')
          .insert({
            user_id: user.id,
            product_id: input.product_id,
            printer_id: input.printer_id,
            name: input.name,
            model_link: input.model_link,
            print_time_hrs: input.print_time_hrs,
            print_time_mins: input.print_time_mins,
            units_per_plate: input.units_per_plate,
            handling_minutes_per_unit: input.handling_minutes_per_unit,
            sale_price_per_unit: input.sale_price_per_unit,
            moms_cut_pct_override: input.moms_cut_pct_override,
          })
          .select('id')
          .single()
        if (error) throw error
        scenarioId = data.id
      }

      // Replace slot rows. Surface delete errors so a stale row doesn't
      // sneak past and produce ghost slots in the scenario.
      const { error: slotDelErr } = await supabase
        .from('calculator_scenario_slots')
        .delete()
        .eq('scenario_id', scenarioId)
      if (slotDelErr) throw slotDelErr
      const slotRows = input.slots
        .filter(
          (s) =>
            s.spool_id || s.model_grams > 0 || s.support_grams > 0 || s.purge_grams > 0,
        )
        .map((s) => ({
          user_id: user.id,
          scenario_id: scenarioId,
          slot_index: s.slot_index,
          spool_id: s.spool_id,
          model_grams: s.model_grams,
          support_grams: s.support_grams,
          purge_grams: s.purge_grams,
        }))
      if (slotRows.length > 0) {
        const { error } = await supabase.from('calculator_scenario_slots').insert(slotRows)
        if (error) throw error
      }

      // Replace hardware rows. Same reason: stale rows would survive a
      // silently-failed delete and break the scenario's cost math.
      const { error: hwDelErr } = await supabase
        .from('calculator_scenario_hardware')
        .delete()
        .eq('scenario_id', scenarioId)
      if (hwDelErr) throw hwDelErr
      const hwRows = input.hardware
        .filter((h) => h.name.trim() !== '')
        .map((h, i) => ({
          user_id: user.id,
          scenario_id: scenarioId,
          sort_order: i,
          name: h.name,
          quantity: h.quantity,
          unit_cost: h.unit_cost,
        }))
      if (hwRows.length > 0) {
        const { error } = await supabase.from('calculator_scenario_hardware').insert(hwRows)
        if (error) throw error
      }

      return scenarioId
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CALC_SCENARIOS_KEY }),
  })
}

export function useDeleteCalculatorScenario() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // CASCADE on the child tables cleans up slots + hardware automatically.
      const { error } = await supabase.from('calculator_scenarios').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CALC_SCENARIOS_KEY }),
  })
}
